import { useMutation } from '@tanstack/react-query';
import { getApiErrorCode, heldOrdersAPI, laundryAPI, orderAPI, receiptsAPI } from '@matix/api-client';
import type {
  CreateOrderRequest,
  CreateOrderResponse,
  OrderDeliveryInput,
  ReplaceReceiptRequest,
  UpdateHeldOrderRequest,
  UpdateHeldOrderResponse,
} from '@matix/types';
import { ensureManagerApproval, heldErrorText, heldSaveExtras } from '../held-orders/heldEdit';
import { trNow } from '../../lib/language';
import type { CartLine } from '../../store/cart';
import { useCart, lineDiscountAmount, lineNetTotal } from '../../store/cart';
import { useHeldOrders } from '../../store/heldOrders';
import type { Customer } from '@matix/types';
import { useTaxConfig } from '../../store/taxConfig';
import { useInvoiceSources } from '../../store/invoiceSources';
import { moneyPrecision } from '../../store/currency';
import { roundMoney } from '@matix/utils';

/**
 * Build a CreateOrderRequest from current cart state.
 *
 * The frontend computes line and header totals locally for display, then
 * sends them as `expected_*` for the server to verify. The server
 * recomputes from items + percentages and rejects mismatches > 0.01.
 *
 * VAT rate is read from the tenant's tax-config store (loaded once on app
 * boot from `/api/v1/tenant/tax-config` → `mstr_TaxSetting.SalesPercentage`).
 * Caller can still pass `taxPercentageOverride` for tests.
 */
export function buildCreateOrderRequest(args: {
  idempotencyKey: string;
  branchId: number;
  cartLines: CartLine[];
  customer: Customer | null;
  /**
   * Which of the customer's addresses the cashier picked, when they have
   * more than one. Omitted (or null) leaves the server exactly where it
   * was: it keeps whatever the phone lookup happened to return.
   */
  customerAddressId?: number | null;
  /**
   * Payment splits the cashier registered, in flow order. At least one
   * is required; sum must equal the order net within 0.01 (the server
   * re-checks).
   */
  payments: Array<{ type_code: number; amount: number; knet_terminal_txn_id?: number }>;
  /**
   * Refund splits for the RETURN leg (Code 53) — how the returned items'
   * value is handed back. Present for return-only and exchange carts; omit
   * (or []) for a plain sale. Sum must equal the return leg's net.
   */
  refundPayments?: Array<{ type_code: number; amount: number; knet_terminal_txn_id?: number }>;
  /**
   * Original invoice id when the return/exchange was started from the archive.
   * Sent as `parent_receipt_id`; the server links + caps against it.
   */
  parentReceiptId?: number | null;
  /**
   * Cash tendered (≥ Σ payments). Defaults to Σ payments (exact change).
   * Set this when the cashier accepts cash over the order amount and
   * the receipt should record the change given.
   */
  cashTendered?: number;
  /**
   * Header-level discount (after line discounts already applied). The CASHIER's
   * discount only — a loyalty redemption is passed separately below, because
   * the server prices it itself and needs to be able to tell the two apart.
   */
  headerDiscountValue?: number;
  /**
   * Loyalty points being redeemed. Sent to the server as `loyalty.redeem_points`.
   */
  loyaltyRedeemPoints?: number;
  /**
   * What those points are worth, as returned by `POST /loyalty/quote`. Never
   * sent on the wire — the server re-derives it — but folded into the
   * `expected_*` totals so a disagreement fails loudly instead of silently
   * under-charging.
   */
  loyaltyRedeemValue?: number;
  /**
   * The points are a "Loyality" payment, not a discount (`LoyaltyRedemption.asPayment`):
   * `loyaltyRedeemValue` is then 0 and the expected totals are the full invoice.
   */
  loyaltyAsPayment?: boolean;
  /**
   * Gift cards being SPENT on this order. Each entry names a card and how much
   * of it is going onto the receipt; the matching payment split (on the tenant's
   * gift-card payment code) must already be in `payments`, and the server checks
   * that the two add up to the same figure.
   */
  giftCardRedeem?: Array<{ card_number: string; pin?: string | null; amount: number }>;
  /**
   * Gift cards being CREDITED on the return leg. The matching split must be in
   * `refundPayments`. Each credit is capped server-side at what that card
   * actually paid on the parent invoice.
   */
  giftCardRefund?: Array<{ card_number: string; amount: number }>;
  deliveryFee?: number;
  /**
   * The delivery sub-object, required by the server whenever
   * `orderTypeCode === 2`. Carries the customer / address / driver / promised
   * time the DeliveryConfirmModal collected. It lives here rather than being
   * spread onto the result by the caller so that BOTH paths that book a
   * delivery — "create unpaid" in that modal and the full payment flow in
   * PaymentModal — put the same shape on the wire.
   */
  delivery?: OrderDeliveryInput | null;
  /** Delivery vs walk-in vs takeaway — opaque numeric tag stored on receipt. */
  orderTypeCode?: number | null;
  /**
   * The payment modal's boxes. Both default to what the cart store holds, so
   * a caller that doesn't pass them still sends whatever the cashier typed.
   */
  note?: string | null;
  referenceNo?: string | null;
  /** Override VAT rate (whole percent — 15 = 15%). Defaults to tenant config. */
  taxPercentageOverride?: number;
  /**
   * Selected BranchTables row for dine-in orders. Sent as `branch_table_id`
   * (stamped onto Inv_Receipt_h.BranchTableId by the server) and `table_label`
   * (printed on the customer receipt + kitchen ticket as "Table: N" / the
   * tenant's term). Both null for walk-in / takeaway / delivery.
   */
  table?: { id: number; label: string } | null;
  /**
   * Phase 8 audit fields — forwarded from the cart's `discount` shape.
   * All optional; omit when not relevant (e.g. no discount, or coupon
   * not used). The server stamps these onto Inv_Receipt_h for audit.
   */
  /** mstr_DiscountReasons.ID for manual discounts; null when omitted. */
  discountReasonId?: number | null;
  /** Security_Users.Id of the manager who approved an over-limit discount. */
  managerOverrideUserId?: number | null;
  /** Coupon code redeemed (Phase 8d — currently disabled in modal). */
  couponCode?: string | null;
  /**
   * A coupon from the NEW coupon engine (migration 038).
   *
   * Send the CODE only: the server re-validates it against the basket, prices
   * the discount itself and books the redemption. Because the server computes
   * the amount, it must NOT also arrive inside `discount_value` — that field
   * carries the cashier's own discount only, exactly like the loyalty
   * redemption. The cart's `expected_*` totals DO include the coupon's value,
   * so a client/server disagreement still fails as TOTALS_MISMATCH.
   */
  engineCouponCode?: string | null;
  /**
   * Membership buy (order_type === 5): the package validity window. The
   * server turns the receipt's lines into the prepaid package the customer
   * can redeem until `end_date`.
   */
  membership?: { start_date: string; end_date: string } | null;
}): CreateOrderRequest {
  // Read straight from the store (sync) — buildCreateOrderRequest is called
  // from a click handler, not from a React render, so a hook would be wrong.
  const tenantPercent = useTaxConfig.getState().config.sales_percentage;
  const { note: cartNote, referenceNo: cartReferenceNo } = useCart.getState();
  const taxPercentage = args.taxPercentageOverride ?? tenantPercent;
  // Money is rounded at the tenant currency's precision (3 on KWD, 2 on
  // EGP/SAR/AED), matching cart.totals() and the server's `roundMoney`.
  const rm = (n: number): number => roundMoney(n, moneyPrecision());

  // Map cart lines to API line items. Return lines are sent with a NEGATIVE
  // qty + total (the server groups by sign into sale/return legs); the price
  // stays a positive unit price. Modifiers are already baked into unitPrice.
  const items = args.cartLines.map((line) => {
    const sign = line.isReturn ? -1 : 1;
    // Net of the line's own discount, from the same helper the cart totals
    // use — the server recomputes `qty * price - discount` and rejects the
    // line if the two disagree by more than a rounding tolerance.
    const total = sign * lineNetTotal(line, rm);
    const lineDiscount = lineDiscountAmount(line, rm);
    return {
      item_id: line.product.id,
      qty: sign * line.qty,
      price: line.unitPrice,
      total,
      // Audit only: the price before the cashier typed over it.
      ...(line.priceOverrideFrom != null && Math.abs(line.priceOverrideFrom - line.unitPrice) > 1e-9
        ? { price_override_from: line.priceOverrideFrom }
        : {}),
      // The unit the tile / barcode named. The server takes the conversion
      // factor from the item's own unit row — never from the till — and
      // falls back to the selling unit when a line carries none.
      ...(line.product.unitId != null ? { unit_id: line.product.unitId } : {}),
      notes: line.note ?? null,
      // The clamped MONEY is what the server subtracts (`discount_value` wins
      // over `discount_percent` there), so it is always sent; the percent
      // rides along only to be stored on Inv_Receipt_d.Discount_Percent, which
      // is what the ERP's reports read.
      ...(lineDiscount > 0
        ? {
            discount_value: lineDiscount,
            ...(line.lineDiscount?.type === 'percent'
              ? { discount_percent: line.lineDiscount.value }
              : {}),
          }
        : {}),
      ...(line.membershipDetailId
        ? { membership_detail_id: line.membershipDetailId }
        : {}),
      ...(line.salesman1Id != null ? { salesman_id: line.salesman1Id } : {}),
      ...(line.salesman2Id != null ? { salesman2_id: line.salesman2Id } : {}),
      // Only a RETURN line of a RestockOnReturn item can be written off, and
      // only `false` changes anything server-side — so that's the only case
      // worth putting on the wire. The server re-reads the item flag anyway.
      ...(line.isReturn && line.product.restockOnReturn && line.restock === false
        ? { restock: false }
        : {}),
      ...(line.mods.length > 0
        ? {
            modifiers: line.mods.map((m) => ({
              option_id: m.option_id,
              group_id: m.group_id,
              group_name: m.group,
              name: m.name,
              name_ar: m.nameAr || null,
              price_delta: m.price,
              // "Choices with quantity" only; ordinary options send what they always did.
              ...(m.qty != null ? { quantity: m.qty } : {}),
            })),
          }
        : {}),
    };
  });

  // Header totals per leg — must match the server formula. Sale leg carries
  // the header discount + delivery; return leg refunds line values + VAT.
  const saleSubtotal = rm(
    items.filter((l) => l.total > 0).reduce((s, l) => s + l.total, 0),
  );
  const returnSubtotal = rm(
    items.filter((l) => l.total < 0).reduce((s, l) => s + Math.abs(l.total), 0),
  );
  const rawHeaderDiscount = rm(args.headerDiscountValue ?? 0);
  // A coupon priced by the server is NOT the cashier's discount. It goes on the
  // wire as a code and comes back priced, so it must not also ride inside
  // `discount_value` — that would double it. The two are mutually exclusive at
  // the till (applying a coupon clears the manual discount), so when an engine
  // coupon is present the whole header discount IS the coupon.
  const engineCouponValue = args.engineCouponCode ? rawHeaderDiscount : 0;
  const discountValue = args.engineCouponCode ? 0 : rawHeaderDiscount;
  // A PURE return (no sale lines) takes the cashier's header discount off the
  // REFUND — the server's `computeExchange` puts it on the return leg, capped
  // at the return's value. Anything else keeps it on the sale leg, exactly as
  // before, so a sale or an exchange computes byte-for-byte as it did.
  const pureReturn = !items.some((l) => l.qty > 0) && items.some((l) => l.qty < 0);
  const saleDiscountValue = pureReturn ? 0 : discountValue;
  // Returning from an invoice, its invoice-level discount comes back as the
  // return's whole-receipt discount, the same share of the returned value
  // (cart.totals `returnInheritedDiscount`, same formula). Sent apart as
  // `return_discount_value` because in an exchange the cashier's own discount
  // belongs to the SALE leg while this one belongs to the return.
  const share = args.parentReceiptId != null ? useCart.getState().returnHeaderShare : 0;
  const returnInheritedDiscount = share > 0 ? rm(returnSubtotal * share) : 0;
  const returnDiscountValue = rm(
    returnInheritedDiscount +
      (pureReturn ? Math.min(discountValue, rm(returnSubtotal - returnInheritedDiscount)) : 0),
  );
  const deliveryFee = rm(args.deliveryFee ?? 0);
  // Redemption is a second header discount: it reduces the taxable base, so
  // VAT is charged on what the customer actually pays. Mirrors the server's
  // `computeLegTotals`, where discountValue = cashier discount + loyalty value.
  const loyaltyValue = rm(args.loyaltyRedeemValue ?? 0);
  const taxableBase =
    saleSubtotal - saleDiscountValue - engineCouponValue - loyaltyValue + deliveryFee;
  const taxValue = rm((taxableBase * taxPercentage) / 100);
  const total = rm(taxableBase + taxValue);
  const returnTaxable = returnSubtotal - returnDiscountValue;
  const returnTaxValue = rm((returnTaxable * taxPercentage) / 100);
  const returnTotal = rm(returnTaxable + returnTaxValue);
  const netSettled = rm(total - returnTotal);

  // Customer mapping. Walk-in (no customer attached) sends null; the
  // server skips the cash-customer linkage and lets the receipt sit
  // anonymous. If a customer is attached, send the id; the server's
  // lookup-by-phone path is reserved for ad-hoc create from order flow,
  // which we don't trigger from the cart since we already have the id.
  // `customer_type` tells the server which table the id belongs to: a credit
  // customer is a GL account (Inv_Receipt_h.Vendor_ID), never a cash-customer
  // row — writing its id into CashCustomerID trips the FK on every tenant.
  // The address only means anything for a CASH customer — a credit
  // customer's id is a GL account and has no Inv_CashCustomerDetail rows.
  const customerPayload = args.customer
    ? {
        customer_id: args.customer.id,
        customer_type: args.customer.type,
        ...(args.customer.type === 'cash' && args.customerAddressId != null
          ? { address_id: args.customerAddressId }
          : {}),
      }
    : null;

  // Cards being SOLD come straight off the cart lines that carry a gift-card
  // draft, so the issue list and the paid-for lines are derived from one source
  // and cannot drift apart. The server re-checks that they sum to the same
  // figure and refuses the order if they do not.
  const giftCardIssues = args.cartLines
    .filter((l) => l.giftCardIssue && !l.isReturn)
    .map((l) => ({
      amount: rm(l.unitPrice * l.qty),
      ...(l.giftCardIssue?.cardNumber ? { card_number: l.giftCardIssue.cardNumber } : {}),
      ...(l.giftCardIssue?.recipientName
        ? { recipient_name: l.giftCardIssue.recipientName }
        : {}),
      ...(l.giftCardIssue?.recipientPhone
        ? { recipient_phone: l.giftCardIssue.recipientPhone }
        : {}),
      ...(l.giftCardIssue?.expiresAt ? { expires_at: l.giftCardIssue.expiresAt } : {}),
      // A card bought by a known customer is linked to them, which is what makes
      // "which cards does this customer hold?" answerable later.
      ...(args.customer ? { customer_id: args.customer.id } : {}),
    }));

  const giftCardRedeem = args.giftCardRedeem ?? [];
  const giftCardRefund = args.giftCardRefund ?? [];
  const giftCardBlock =
    giftCardIssues.length > 0 || giftCardRedeem.length > 0 || giftCardRefund.length > 0
      ? {
          ...(giftCardRedeem.length > 0 ? { redeem: giftCardRedeem } : {}),
          ...(giftCardRefund.length > 0 ? { refund: giftCardRefund } : {}),
          ...(giftCardIssues.length > 0 ? { issue: giftCardIssues } : {}),
        }
      : null;

  return {
    idempotency_key: args.idempotencyKey,
    branch_id: args.branchId,
    // store_id omitted — server resolves from branch.StoreID
    payments: args.payments,
    ...(args.refundPayments && args.refundPayments.length > 0
      ? { refund_payments: args.refundPayments }
      : {}),
    ...(args.parentReceiptId != null ? { parent_receipt_id: args.parentReceiptId } : {}),
    ...(args.cashTendered !== undefined ? { cash_tendered: args.cashTendered } : {}),
    customer: customerPayload,
    items,
    discount_value: discountValue,
    delivery_fee: deliveryFee,
    tax_percentage: taxPercentage,
    expected_subtotal: saleSubtotal,
    expected_tax: taxValue,
    expected_total: total,
    ...(returnInheritedDiscount > 0 ? { return_discount_value: returnInheritedDiscount } : {}),
    expected_return_subtotal: returnSubtotal,
    expected_return_tax: returnTaxValue,
    expected_return_total: returnTotal,
    expected_net: netSettled,
    order_type: args.orderTypeCode ?? null,
    ...(args.delivery ? { delivery: args.delivery } : {}),
    ...(args.membership ? { membership: args.membership } : {}),
    ...(args.loyaltyRedeemPoints && args.loyaltyRedeemPoints > 0
      ? { loyalty: { redeem_points: args.loyaltyRedeemPoints, ...(args.loyaltyAsPayment ? { as_payment: true } : {}) } }
      : {}),
    ...(giftCardBlock ? { gift_cards: giftCardBlock } : {}),
    // The two boxes on the payment modal. `reference_no` lands in
    // Inv_Receipt_h.Memo and `note` in Reference_No + Notes — the columns
    // read backwards, see the mapping note in orders.service.ts.
    note: args.note ?? cartNote,
    reference_no: args.referenceNo ?? cartReferenceNo,
    // Send the UTC instant; the server converts to tenant wall-clock
    // using TENANT_TIMEZONE. This way a cashier whose laptop has the
    // wrong clock or wrong timezone still gets a receipt stamped with
    // the right wall-clock for their branch's location.
    entry_date: new Date().toISOString(),
    // ── Phase 8 audit fields ──────────────────────────────────────────
    // Conditionally spread so the wire payload stays clean for receipts
    // without discounts (the JSON shape doesn't carry null fields when
    // they're irrelevant, which makes server logs easier to read).
    ...(args.discountReasonId != null
      ? { discount_reason_id: args.discountReasonId }
      : {}),
    ...(args.managerOverrideUserId != null
      ? { manager_override_user_id: args.managerOverrideUserId }
      : {}),
    ...(args.couponCode ? { coupon_code: args.couponCode } : {}),
    ...(args.engineCouponCode ? { coupon: { code: args.engineCouponCode } } : {}),
    // Dine-in table attachment — `branch_table_id` stamps Inv_Receipt_h
    // and the junction; `table_label` flows through dispatchPrinting onto
    // the receipt + kitchen ticket. Conditionally spread so non-dine-in
    // wire payloads stay clean.
    ...(args.table
      ? { branch_table_id: args.table.id, table_label: args.table.label }
      : {}),
    // Which price list rang this up. The prices in `items[]` were already
    // resolved from this source's tier when each line was added, so this is a
    // label for reporting — nothing server-side re-prices from it.
    ...(useInvoiceSources.getState().activeId != null
      ? { invoice_source_id: useInvoiceSources.getState().activeId }
      : {}),
    // "Change Salesman" — credit the receipt to someone other than the
    // signed-in cashier. Absent = the cashier; the server checks the permission.
    ...(useCart.getState().creator != null
      ? { employee_id: useCart.getState().creator!.id }
      : {}),
  };
}

/**
 * Strip create-only fields and build a ReplaceReceiptRequest from an
 * already-built CreateOrderRequest. The replace endpoint uses the same
 * item/payment/totals shape but doesn't need idempotency_key, branch_id,
 * or entry_date (those are fixed on the existing receipt).
 */
function buildReplaceRequest(req: CreateOrderRequest): ReplaceReceiptRequest {
  return {
    items: req.items.map((item) => ({
      item_id: item.item_id,
      qty: item.qty,
      price: item.price,
      ...(item.unit_id != null ? { unit_id: item.unit_id } : {}),
      discount_value: item.discount_value,
      discount_percent: item.discount_percent,
      notes: item.notes,
      total: item.total,
      modifiers: item.modifiers,
      // An edit REWRITES every line, so the salesmen have to travel with it.
      // Dropping them here left the rebuilt rows with SalesManId NULL, and the
      // Sales-by-Salesman report filters that column out — so an edited
      // receipt vanished from it entirely while its own print looked right.
      ...(item.salesman_id != null ? { salesman_id: item.salesman_id } : {}),
      ...(item.salesman2_id != null ? { salesman2_id: item.salesman2_id } : {}),
    })),
    customer: req.customer
      ? {
          customer_id: req.customer.customer_id,
          customer_phone: req.customer.customer_phone,
          customer_name: req.customer.customer_name,
          customer_name_ar: req.customer.customer_name_ar,
        }
      : null,
    payments: req.payments,
    cash_tendered: req.cash_tendered,
    discount_value: req.discount_value,
    discount_percent: req.discount_percent,
    delivery_fee: req.delivery_fee,
    tax_percentage: req.tax_percentage,
    expected_subtotal: req.expected_subtotal,
    expected_tax: req.expected_tax,
    expected_total: req.expected_total,
    order_type: req.order_type,
    reference_no: req.reference_no,
    note: req.note,
    discount_reason_id: req.discount_reason_id,
    manager_override_user_id: req.manager_override_user_id,
    coupon_code: req.coupon_code,
    ...(req.employee_id != null ? { employee_id: req.employee_id } : {}),
  };
}

/**
 * The re-hold body for a HELD invoice reopened in the cart: the edit body
 * minus the tender, and minus the order type — a recalled receipt comes back
 * into the cart as take-away with no table (ReceiptDetail carries neither),
 * so sending the cart's type would quietly turn a held dine-in order into a
 * take-away and orphan its table link. Omitted, the server keeps what the
 * receipt already says.
 */
export function buildHeldUpdateRequest(req: CreateOrderRequest): UpdateHeldOrderRequest {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { payments, cash_tendered, order_type, ...rest } = buildReplaceRequest(req);
  // The table ALWAYS travels: the cart now comes back on its table, so what
  // it holds is the truth — a moved table moves the order, a cleared one
  // frees it. The server refuses a table another held order sits on.
  return { ...rest, branch_table_id: useCart.getState().table?.id ?? null };
}

/**
 * What a held invoice cannot carry through its payment: each of these is
 * booked by the ORDER path under a lock at write time, and the held-order pay
 * path books money only. Refused up front with a sentence the cashier can act
 * on, rather than silently dropped.
 */
export function heldPaymentRefusal(req: CreateOrderRequest): string | null {
  const gc = req.gift_cards;
  if (gc && ((gc.redeem?.length ?? 0) > 0 || (gc.issue?.length ?? 0) > 0 || (gc.refund?.length ?? 0) > 0)) {
    return trNow({
      en: 'A held order cannot be paid with or sell a gift card. Cancel it and ring a new sale.',
      ar: 'لا يمكن دفع طلب معلق ببطاقة هدية أو بيع بطاقة فيه. ألغِه وسجّل بيعاً جديداً.',
    });
  }
  if ((req.loyalty?.redeem_points ?? 0) > 0) {
    return trNow({ en: 'Points cannot be redeemed on a held order.', ar: 'لا يمكن استبدال النقاط في طلب معلق.' });
  }
  if (req.coupon?.code) {
    return trNow({ en: 'A coupon cannot be applied to a held order.', ar: 'لا يمكن تطبيق كوبون على طلب معلق.' });
  }
  if (req.items.some((i) => i.qty < 0)) {
    return trNow({ en: 'A held order cannot carry a return.', ar: 'لا يمكن أن يتضمن الطلب المعلق مرتجعاً.' });
  }
  return null;
}

/**
 * Save the cart's lines onto a HELD invoice (still unpaid) — the one place
 * both Hold and Charge go through, so the edit rules are applied once:
 * the "print the new items?" question, and the manager's password for a
 * removal. When the server says a removal needs approval the till did not
 * collect (or the one it had is wrong), it asks here and tries once more.
 */
export async function saveHeldOrder(id: number, req: CreateOrderRequest): Promise<UpdateHeldOrderResponse> {
  const body = { ...buildHeldUpdateRequest(req), ...heldSaveExtras() };
  try {
    return await heldOrdersAPI.update(id, body);
  } catch (err) {
    const code = getApiErrorCode(err);
    if (code !== 'MANAGER_APPROVAL_REQUIRED' && code !== 'MANAGER_PASSWORD_INVALID') throw err;
    useCart.getState().setHeldManagerPassword(null);
    if (!(await ensureManagerApproval())) {
      throw new Error(
        trNow({
          en: 'Not saved — removing items from a held order needs the manager’s approval.',
          ar: 'لم يتم الحفظ — حذف أصناف من طلب معلق يتطلب موافقة المدير.',
        }),
      );
    }
    return heldOrdersAPI.update(id, { ...body, manager_password: useCart.getState().heldManagerPassword! });
  }
}

/**
 * Charge a HELD invoice reopened in the cart: save the cart's lines onto it
 * (still unpaid), then take the money — two calls, both against the same
 * receipt. The first leaves it held, so if the second fails nothing has been
 * booked and the cashier can simply try again.
 */
async function chargeHeld(id: number, req: CreateOrderRequest): Promise<CreateOrderResponse> {
  const refusal = heldPaymentRefusal(req);
  if (refusal) throw new Error(refusal);
  let paid;
  try {
    await saveHeldOrder(id, req);
    const tender = {
      payments: req.payments,
      ...(req.cash_tendered !== undefined ? { cash_tendered: req.cash_tendered } : {}),
    };
    // A LAUNDRY order being collected: the same pay, plus the move to
    // Delivered, in one server transaction.
    const laundryStatus = useCart.getState().laundryCollectStatus;
    paid =
      laundryStatus != null
        ? await laundryAPI.collect(id, { ...tender, expected_status: laundryStatus })
        : await heldOrdersAPI.pay(id, tender);
  } catch (err) {
    // Say it in the cashier's language when it is one of ours.
    const text = heldErrorText(err);
    if (text) throw new Error(text);
    throw err;
  }
  // The Held Orders list and its badge no longer include this one.
  void useHeldOrders.getState().refresh();
  return {
    order_id: paid.order_id,
    entry_no: paid.entry_no,
    entry_date: paid.entry_date,
    totals: paid.totals,
    idempotent_replay: false,
    print_count: paid.print_count,
  };
}

/**
 * React Query mutation for placing an order.
 *
 * Why mutation (not plain fetch):
 *   - Built-in pending/error/success states for the modal UI.
 *   - `onSuccess` and `onError` hooks so the caller can chain side
 *     effects (clear cart, show receipt, surface error toast) without
 *     wrapping the call in try/catch every time.
 *
 * The idempotency key is generated by the caller (typically once per
 * "Charge" click, regenerated if the user backs out and tries again
 * with different items).
 */
export function usePlaceOrder(opts?: {
  onSuccess?: (response: CreateOrderResponse) => void;
  onError?: (err: unknown) => void;
}) {
  return useMutation({
    mutationFn: async (reqIn: CreateOrderRequest): Promise<CreateOrderResponse> => {
      const { editingReceiptId, editingHeld, laundryPayNow, membershipDates } = useCart.getState();
      // LAUNDRY pay-now: whichever request the payment window built, it is a
      // laundry Pickup order with its ready-by (a Delivery one stays Delivery).
      // MEMBERSHIP: the validity window the cashier confirmed before paying.
      if (membershipDates && reqIn.order_type === 5 && editingReceiptId == null) {
        reqIn = { ...reqIn, membership: { start_date: membershipDates.start, end_date: membershipDates.end } };
      }
      const req: CreateOrderRequest =
        laundryPayNow && editingReceiptId == null
          ? {
              ...reqIn,
              laundry_order: true,
              order_type: reqIn.order_type === 2 ? 2 : 4,
              delivery_date: laundryPayNow.readyBy,
            }
          : reqIn;
      if (editingReceiptId != null && editingHeld) {
        return chargeHeld(editingReceiptId, req);
      }
      if (editingReceiptId != null) {
        return receiptsAPI.replace(editingReceiptId, buildReplaceRequest(req));
      }
      // Bounded on purpose — see orderAPI.create. A charge that has not been
      // answered in 15 s has almost certainly lost its connection, and the
      // cashier is better served by the offline queue than by another 15 s of
      // "Saving…". Comfortably above the slowest real order write observed
      // (~5 s), and safe if the sale did land: the replay is deduped.
      return orderAPI.create(req, { timeoutMs: 15_000 });
    },
    onSuccess: opts?.onSuccess,
    onError: opts?.onError,
  });
}
